import { useContext, useMemo, useState } from "react";

import { getReferenceString } from "@medplum/core";
import { EpisodeOfCare, Practitioner } from "@medplum/fhirtypes";
import { ResourceInput, useMedplum } from "@medplum/react";

import {
  Button,
  Drawer,
  FileInput,
  Flex,
  SegmentedControl,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";

import { IconCalendar, IconCheck, IconUpload } from "@tabler/icons-react";
import { parseISO } from "date-fns";
import { z } from "zod";

import EpisodeOfCareSearchInput from "@/components/EpisodeOfCareSearchInput";
import MultiSelectCreatable from "@/components/MultiSelectCreatable";
import { TextWrapper } from "@/components/TextWrapper";
import { PatientContext } from "@/contexts/patient";
import { useAppDispatch } from "@/store";
import { addLabOrder } from "@/store/slices/lab-orders";
import { getOrganizationReference, getPracticeIdForPatient } from "@/utils/medplum";
import { showSystemErrorNotification, toast } from "@/utils/notifications";
import { REQUIRED_FIELD_MESSAGE, zResource } from "@/utils/zod";

import { useEpisodeOfCareAutocompleteField } from "../hooks/useEpisodeOfCareAutocompleteField";
import { uploadManualResult } from "../services/manual-labs";
import { UploadedResultType } from "../types/labs";

/**
 * Mantine v8 Path<T> recurses through Practitioner → extension → Extension → valueCoding → Coding → extension → ...
 * Using Pick with only scalar fields breaks the mutual recursion while keeping the runtime
 * Practitioner object directly assignable (Practitioner structurally extends the pick).
 */
type UploadResultsFormValues = {
  resultType: UploadedResultType;
  testOrPanels: string[];
  requestor: Pick<Practitioner, "resourceType" | "id"> | undefined;
  episodeOfCare: Pick<EpisodeOfCare, "resourceType" | "id"> | undefined;
  dateOrdered: Date | null;
  dateReceived: Date | null;
  title: string;
  indication: string;
  results: string;
  files: File[];
};

const TEST_OPTIONS = [
  "Blood",
  "Antibody Screen",
  "CBC",
  "Urine",
  "Diabetes Screen",
  "GTT",
  "Pap",
  "HPV",
  "Rubella",
  "Gonorrhea",
  "HBsAg",
  "Chlamydia",
  "TPA/RPR/VDRL",
  "GBS",
  "HIV",
];

const IMAGING_STUDY_OPTIONS = [
  "Dating Ultrasound",
  "NT Scan",
  "Anatomy Scan",
  "Growth Ultrasound",
  "Biophysical Profile",
  "Doppler Ultrasound",
  "Transvaginal Ultrasound",
  "Pelvic Ultrasound",
  "Breast Ultrasound",
  "Mammogram",
  "X-Ray",
  "MRI",
  "CT Scan",
  "Other",
];

const RESULT_TYPE_OPTIONS = [
  { label: "Lab", value: UploadedResultType.Lab },
  { label: "Imaging", value: UploadedResultType.Imaging },
];

const schema = z.object({
  resultType: z.nativeEnum(UploadedResultType),
  testOrPanels: z.array(z.string()).min(1, "Required"),
  requestor: zResource<Practitioner>("Practitioner").optional(),
  episodeOfCare: zResource<EpisodeOfCare>("EpisodeOfCare").optional(),
  dateOrdered: z.date({ message: REQUIRED_FIELD_MESSAGE }),
  dateReceived: z.date({ message: REQUIRED_FIELD_MESSAGE }),
  title: z.string(),
  indication: z.string(),
  results: z.string(),
  files: z.array(z.custom<File>((value) => value instanceof File, "Required")).min(1, "Required"),
});

interface UploadResultsDrawerProps {
  opened: boolean;
  onClose: () => void;
}

export default function UploadResultsDrawer({ opened, onClose }: UploadResultsDrawerProps) {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { patient } = useContext(PatientContext);
  const practiceId = useMemo(() => getPracticeIdForPatient(patient), [patient]);

  const form = useForm<UploadResultsFormValues>({
    initialValues: {
      resultType: UploadedResultType.Lab,
      testOrPanels: [],
      requestor: undefined,
      episodeOfCare: undefined,
      dateOrdered: null as Date | null,
      dateReceived: null as Date | null,
      title: "",
      indication: "",
      results: "",
      files: [] as File[],
    },
    validate: zodResolver(schema),
  });

  const { episodeOfCareInputProps, handleEpisodeOfCareChange } =
    useEpisodeOfCareAutocompleteField(form);
  const isImaging = form.values.resultType === UploadedResultType.Imaging;

  const handleResultTypeChange = (value: string) => {
    form.setFieldValue(
      "resultType",
      value === UploadedResultType.Imaging ? UploadedResultType.Imaging : UploadedResultType.Lab,
    );
    // The test and study lists differ, so a selection from one type means nothing in the other.
    form.setFieldValue("testOrPanels", []);
  };

  const handleSubmit = async () => {
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      if (import.meta.env.DEV) {
        console.error("Validation errors:", validationResult.errors);
      }
      return;
    }

    if (!patient) return;

    setIsSubmitting(true);
    try {
      const {
        resultType,
        testOrPanels,
        requestor,
        episodeOfCare,
        dateOrdered,
        dateReceived,
        title,
        indication,
        results,
        files,
      } = form.values;
      const result = await uploadManualResult(medplum, {
        patient,
        files: files!,
        resultType,
        testOrPanels,
        title,
        indication,
        results,
        requestor: requestor as Practitioner | undefined,
        episodeOfCare: episodeOfCare as EpisodeOfCare | undefined,
        dateOrdered,
        dateReceived,
      });

      dispatch(addLabOrder(result));

      toast({
        title: "Results uploaded",
        message: `${isImaging ? "Imaging" : "Lab"} results have been uploaded successfully.`,
        type: "success",
        icon: <IconCheck />,
        autoClose: 5000,
      });

      handleClose();
    } catch (error) {
      console.error(error);
      showSystemErrorNotification();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const isSubmitDisabled: boolean = useMemo(() => {
    return (
      !form.values.testOrPanels.length ||
      !form.values.files?.length ||
      !form.values.dateOrdered ||
      !form.values.dateReceived
    );
  }, [form]);

  return (
    <Drawer.Root
      trapFocus
      size={512}
      opened={opened}
      position="right"
      onClose={handleClose}
      closeOnEscape={false}
      closeOnClickOutside={false}
    >
      <Drawer.Overlay />
      <Drawer.Content>
        <Drawer.Header h={64} px="md" bg="gray.0">
          <Flex w="100%" gap="md" align="center" justify="space-between">
            <Flex gap="md" align="center" style={{ flex: 1 }}>
              <Drawer.CloseButton aria-label="Close Upload Results Drawer" />
              <Drawer.Title style={{ flex: 1 }}>
                <TextWrapper fz="lg" variant="bold" style={{ wordBreak: "break-word" }}>
                  Upload Results
                </TextWrapper>
              </Drawer.Title>
            </Flex>
            <Button
              type="button"
              color="burgundy"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitDisabled}
            >
              Save
            </Button>
          </Flex>
        </Drawer.Header>
        <Drawer.Body p="lg">
          <Stack gap="lg">
            <SegmentedControl
              radius="xs"
              aria-label="Result type"
              data={RESULT_TYPE_OPTIONS}
              value={form.values.resultType}
              onChange={handleResultTypeChange}
              data-testid="upload-results-type"
            />
            <MultiSelectCreatable
              required
              creatable
              key={form.values.resultType}
              label={isImaging ? "Imaging Study" : "Test or Panel"}
              data={isImaging ? IMAGING_STUDY_OPTIONS : TEST_OPTIONS}
              {...form.getInputProps("testOrPanels")}
            />
            {isImaging && (
              <TextInput
                label="Title"
                data-testid="upload-results-title"
                {...form.getInputProps("title")}
              />
            )}
            <ResourceInput
              name="requestor"
              label="Requestor"
              resourceType="Practitioner"
              placeholder="Search requestor"
              searchCriteria={{
                _compartment: getOrganizationReference(practiceId),
              }}
              {...form.getInputProps("requestor")}
            />
            {patient?.id ? (
              <EpisodeOfCareSearchInput
                {...episodeOfCareInputProps}
                onChange={handleEpisodeOfCareChange}
                searchCriteria={{
                  patient: getReferenceString(patient)!,
                  _compartment: getOrganizationReference(practiceId),
                }}
              />
            ) : null}
            <DateInput
              required
              label="Date Ordered"
              value={form.values.dateOrdered}
              error={form.errors.dateOrdered}
              rightSection={<IconCalendar size={16} />}
              onChange={(val) => form.setFieldValue("dateOrdered", val ? parseISO(val) : null)}
            />
            <DateInput
              required
              label="Date Received"
              value={form.values.dateReceived}
              error={form.errors.dateReceived}
              rightSection={<IconCalendar size={16} />}
              onChange={(val) => form.setFieldValue("dateReceived", val ? parseISO(val) : null)}
            />
            {isImaging && (
              <>
                <TextInput
                  label="Indication"
                  data-testid="upload-results-indication"
                  {...form.getInputProps("indication")}
                />
                <Textarea
                  autosize
                  minRows={4}
                  label="Results"
                  data-testid="upload-results-results"
                  {...form.getInputProps("results")}
                />
              </>
            )}
            <FileInput
              required
              multiple
              label="Upload Results"
              leftSection={<IconUpload size={16} />}
              accept="application/pdf,image/png,image/jpeg"
              {...form.getInputProps("files")}
            />
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}
